import { User, UserManager, WebStorageStateStore } from 'oidc-client'
import { inited, authed, getFrameworkVar, getFrameworkServiceUrl, getOidcClientId } from './states'
import pLimit from 'p-limit'
import fetch, { getUrlObject } from '../libs/fetch'
import {
  DEFAULT_TSG_OIDC_CONFIG,
  INSTANCE_STORE_URL_KEY,
  TENANCY_SERVICE_URL_KEY,
  APP_REGISTRY_URL_KEY,
  TSG_ID_QUERY_NAMES,
  TSG_ID_NONE,
  OIDC_STORE_PREFIX,
  TSG_ID_REGEX,
} from './constants'
import { tryParseJson } from '../init'
import { dedupPromise, jwtDecode, tryParseErrorJson } from './utils'
import { indexTsgTree, indexTsgInstances, indexAppManifests } from './indexedArrays'
import history from './history'
import { parseAccess, fetchIamRolePermissionDefinition, fetchIamCustomRoleDefinitionByJwt, indexCustomRoles } from './access'
import { overrideTsgInstances } from './overrideInstances'
import { __error, __isDebugOn } from '../init/debug'
import type { Instance, Tsg, ReadonlyTsgTree, ReadonlyTsgInstances, TsgInstanceError } from './indexedArrays'
import type {
  AppManifests,
  IamCustomRolesDefinition,
  IamRolePermissionDefinition,
  OidcClientConfig,
  ReadonlyAccess,
} from './stateTypes'
import { getDatadogRum, type RumGlobal } from './externals'

const TSG_LOGIN_POOL_SIZE = 5
const TSG_TOKEN_POOL_SIZE = 10
const FETCH_CACHING_DURATION = 5000 // 5s
const MIN_TSG_JWT_EXP = 30 // s
const MAX_TSG_JWT_EXP = 28800000 // 8hr

export type TsgIdAwareType = {
  tsg_id?: string
}

export type TsgUserManager = UserManager & TsgIdAwareType
export type TsgUser = User
export type { Instance, Tsg, ReadonlyTsgTree, ReadonlyTsgInstances }

export type TsgUserManagers = Record<string, TsgUserManager> & {
  __current_tsg_id__?: string
  getCurrentTsgId: () => string | undefined
}

export { User, indexTsgTree, indexTsgInstances, TSG_ID_NONE }

export const hasTsgSupport = (): boolean => {
  return Boolean(getOidcClientId())
}

export const tsgUserManagers = Object.defineProperties({}, {
  getCurrentTsgId: {
    writable: true,
    value: () => tsgUserManagers.__current_tsg_id__
  }
}) as TsgUserManagers

export const getUserManager = (tsg_id?: string) => {
  if (getFrameworkVar('use_provided_tsg_token')) {
    const userMgr = tsgUserManagers['']
    if (userMgr && (!tsg_id || tsg_id === TSG_ID_NONE || tsg_id === userMgr.tsg_id)) {
      return userMgr
    }
    throw new Error('default tsg user manager is not found for use_provided_tsg_token mode')
  }
  const { config_url, authority = config_url, ...settings } = getFrameworkVar<OidcClientConfig>('oidc_client') || {} as OidcClientConfig
  const client_id = getOidcClientId()
  if (!tsg_id || !client_id || !(authority || config_url)) {
    throw new Error('Missing tsg_id or oidc config')
  }
  const existingMgr = tsgUserManagers[tsg_id]
  if (existingMgr) {
    return existingMgr
  }
  const metadata = tsgUserManagers['']?.settings?.metadata
  const store = new WebStorageStateStore({ prefix: `${OIDC_STORE_PREFIX}${tsg_id}.`, store: sessionStorage })
  const tsgUserManager = new UserManager({
    ...DEFAULT_TSG_OIDC_CONFIG,
    redirect_uri: `${window.origin}${DEFAULT_TSG_OIDC_CONFIG.redirect_uri}`,
    silent_redirect_uri: `${window.origin}${DEFAULT_TSG_OIDC_CONFIG.silent_redirect_uri}`,
    ...settings,
    authority,
    client_id,
    loadUserInfo: false,
    scope: `profile email tsg_id:${tsg_id}`,
    metadata,
    stateStore: store,
    userStore: store,
  }) as TsgUserManager
  tsgUserManager.tsg_id = tsg_id
  tsgUserManagers[tsg_id] = tsgUserManager
  return tsgUserManager
}

const cachedParsedTsgTokens = new Map()
const MAX_PARSED_TSG_CACHE = 10
export const jwtDecodeCached = (access_token: string, tsg_id?: string) => {
  const cached = cachedParsedTsgTokens.get(tsg_id) // allow tsg_id is undefined
  if (cached?.parsed && cached.access_token === access_token && cached.parsed.tsg_id === tsg_id) {
    return cached.parsed
  }
  const parsed = jwtDecode(access_token) as TsgIdAwareType
  if (tsg_id && tsg_id !== parsed.tsg_id) {
    return parsed // skip cache if tsg_id is different
  }
  cachedParsedTsgTokens.set(parsed.tsg_id, { // can be undefined
    tsg_id,
    access_token,
    parsed,
  })
  if (cachedParsedTsgTokens.size > MAX_PARSED_TSG_CACHE) {
    const oldest = cachedParsedTsgTokens.keys().next().value
    cachedParsedTsgTokens.delete(oldest)
  }
  return parsed
}

export type UserExpRefToken = { expires_at?: number, refresh_token?: string } | null | undefined
export const shouldDiscardUser = (user: UserExpRefToken, now: number = Date.now()): boolean => {
  if (!user) {
    return false
  }
  if (user?.expires_at && user.expires_at * 1000 < now - MAX_TSG_JWT_EXP) {
    return true
  }
  else if (user?.refresh_token) {
    const decoded = jwtDecode(user.refresh_token)
    return decoded.exp * 1000 < now
  }
  return false
}

export type GetUserFromManagerOpts = {
  tsg_id?: string | undefined
  forceSignIn?: boolean
}
export type LimitFn = (fn: () => Promise<TsgUser>) => Promise<TsgUser>
const globalLoginLimitFn = pLimit(TSG_LOGIN_POOL_SIZE) as LimitFn
const globalTokenLimitFn = pLimit(TSG_TOKEN_POOL_SIZE) as LimitFn
export const getUserFromManager = async (tsgUserManager: TsgUserManager, {
  tsg_id = tsgUserManager.tsg_id,
  forceSignIn,
}: GetUserFromManagerOpts = {}): Promise<TsgUser> => {
  if (!tsg_id) { // should not happen, just for typings
    throw new Error('tsg_id is missing')
  }
  const cachePromise = (promise?: Promise<User>) => {
    Object.defineProperties(tsgUserManager, {
      _get_user_promise: {
        configurable: true,
        value: promise
      }
    })
  }
  let tsgUser = await tsgUserManager.getUser()
  const isTsgUserMatched = () => tsgUser?.scopes.includes(`tsg_id:${tsg_id}`)
  if (tsgUser?.access_token && !tsgUser.expired && !forceSignIn &&
    tsgUser.expires_in > MIN_TSG_JWT_EXP && isTsgUserMatched()) {
    return tsgUser
  }
  const existPromise = (tsgUserManager as any)._get_user_promise
  if (existPromise) {
    return existPromise
  }
  // need renew or login again
  // if (tsg_id === TSG_ID_NONE) {
  //   throw new Error('No access token available, please sign-in again')
  // }
  if (shouldDiscardUser(tsgUser) || (tsgUser && !isTsgUserMatched())) { // discard expired saved user
    tsgUserManager.removeUser()
    tsgUser = null
  }
  const signin = () => tsgUserManager.signinSilent().then((token) => {
    cachePromise()
    return token
  }, (err) => {
    if (tsgUser?.refresh_token) {
      console.warn('failed to silent login, try again', err)
      tsgUserManager.removeUser()
      return tsgUserManager.signinSilent().then((token) => {
        cachePromise()
        return token
      }, (err) => {
        cachePromise()
        throw err
      })
    }
    cachePromise()
    throw err
  })
  const limitFn = tsgUser?.refresh_token ? globalTokenLimitFn : globalLoginLimitFn
  const promise = limitFn(signin)
  cachePromise(promise)
  tsgUser = await promise
  if (!tsgUser?.access_token) {
    throw new Error('No access token returned')
  }
  const decoded = jwtDecodeCached(tsgUser.access_token, tsg_id) as TsgIdAwareType
  if (!decoded.tsg_id || decoded.tsg_id !== tsg_id) {
    await tsgUserManager.removeUser()
    const error = new Error(decoded.tsg_id ? 'The tenant is not accessible' : 'Invalid access token returned') as JwtError
    error.access_token = tsgUser.access_token
    error.access_token_decoded = decoded
    console.error('getting tsg token does not return correct tsg_id', tsg_id, decoded)
    throw error
  }
  return tsgUser
}

export type JwtError = Error & { access_token: string, access_token_decoded: TsgIdAwareType }
export const getTsgToken = async (tsg_id: string | undefined): Promise<string> => {
  if (!inited.result) {
    await inited // wait for init
  }
  const tsgUserManager = getUserManager(tsg_id)
  const promise = (tsgUserManager as any)._get_user_promise || getUserFromManager(tsgUserManager)
  const user = await promise
  return user.access_token || undefined
}

export const revokeTsgToken = async (tsg_id: string | undefined): Promise<void> => {
  if (!inited.result) {
    await inited // wait for init
  }
  const tsgUserMgr = tsg_id ? tsgUserManagers[tsg_id] : undefined
  if (tsg_id && tsgUserMgr) {
    delete tsgUserManagers[tsg_id]
    await tsgUserMgr.revokeAccessToken()
    tsgUserMgr.removeUser()
  }
}

export const revokeAllTsgTokens = async (): Promise<void> => {
  await Promise.allSettled(Object.keys(tsgUserManagers).filter(Boolean).map(tsgId => {
    return tsgUserManagers[tsgId]?.revokeAccessToken().then(() => {
      tsgUserManagers[tsgId].removeUser()
      delete tsgUserManagers[tsgId]
    })
  }))
}

export const clearStoredExpiredTsgTokens = () => {
  const keys = Object.keys(sessionStorage).filter(key => key.startsWith(OIDC_STORE_PREFIX))
  const now = Date.now()
  for (const key of keys) {
    const parsed = tryParseJson(sessionStorage.getItem(key)) as UserExpRefToken
    if (shouldDiscardUser(parsed, now)) {
      sessionStorage.removeItem(key)
    }
  }
}

export const clearCurrentTsgId = () => {
  sessionStorage.removeItem('current_tsg_id')
  localStorage.removeItem('current_tsg_id')
  tsgUserManagers.__current_tsg_id__ = undefined
  if (window.name.startsWith('tsg:')) {
    window.name = ''
  }
}

export const setCurrentTsgId = (tsgId: string | undefined) => {
  if (!tsgId) {
    return clearCurrentTsgId()
  }
  tsgUserManagers.__current_tsg_id__ = tsgId
  if (tsgId !== TSG_ID_NONE) {
    sessionStorage.current_tsg_id = tsgId
    localStorage.current_tsg_id = tsgId
    window.name = `tsg:${tsgId}`
  }
}

const readTsgFromName = (): string | undefined => {
  const n = window.name
  if (/^tsg:\d+$/.test(n)) {
    return n.slice(4)
  }
}
const convertStoredTsgIdValue = (tsgId: string | null | undefined): string | undefined => {
  return !tsgId || tsgId === TSG_ID_NONE ||
    tsgId === 'undefined' || tsgId === 'null' || tsgId === 'false' ||
    !TSG_ID_REGEX.test(tsgId) ? undefined : tsgId
}
const getTsgIdFromCurrentSession = (): string | undefined => {
  return readTsgFromName() || convertStoredTsgIdValue(sessionStorage.current_tsg_id)
}
export const getCurrentTsgId = (): string | undefined => {
  const tsgId: string | undefined = tsgUserManagers.getCurrentTsgId() ||
    tsgUserManagers.__current_tsg_id__ || getTsgIdFromCurrentSession()
  // || localStorage.current_tsg_id // stop use local storage due to preference
  return tsgId
}

const TSG_ID_HASH_REGEX = new RegExp(`^#!(${TSG_ID_QUERY_NAMES.join('|')})=`)
export const getTsgIdFromUrl = ({ search, hash }: Location = window.location): string | undefined => {
  const urlParams = new URLSearchParams(search)
  for (const key of TSG_ID_QUERY_NAMES) {
    const tsgId = urlParams.get(key)?.replace(/\W/g, '')
    if (tsgId && tsgId !== TSG_ID_NONE) {
      return tsgId
    }
  }
  const hashKey = (hash.startsWith('#!tsg') && hash.match(TSG_ID_HASH_REGEX)?.[1]) || ''
  if (hashKey) {
    const hashParams = new URLSearchParams(hash.slice(2))
    const tsgId = hashParams.get(hashKey)?.replace(/\W/g, '')
    if (tsgId && tsgId !== TSG_ID_NONE) {
      return tsgId
    }
  }
  return undefined
}

export const cleanupTsgIdFromUrl = ({ search, pathname }: Location = window.location) => {
  const urlParams = new URLSearchParams(search)
  for (const key of TSG_ID_QUERY_NAMES) {
    urlParams.delete(key)
  }
  history.replace({
    pathname,
    search: urlParams.toString(),
  }) // auto remove hash
}

export const getAndSaveInitTsgId = (): string | undefined => {
  const tsg_id = getTsgIdFromUrl()
  const curTsgId = getCurrentTsgId() ||
    (getFrameworkVar<boolean>('use_user_preferences') ? undefined :
      convertStoredTsgIdValue(localStorage.current_tsg_id))
  if (tsg_id && curTsgId !== tsg_id && tsg_id !== TSG_ID_NONE) {
    setCurrentTsgId(tsg_id)
    return tsg_id
  }
  setCurrentTsgId(curTsgId) // ensure insync
  return curTsgId
}

export const getAuthHeaders = async (tsg_id: string | undefined, headers?: object) => {
  const jwt = await getTsgToken(tsg_id)
  return {
    ...headers,
    Authorization: `Bearer ${jwt}`
  } as Record<string, string>
}

export const getCurrentTsgToken = async () => {
  if (!authed.result) {
    await authed // wait for login
  } // wait for authed before get the tsg_id
  const tsgId = getCurrentTsgId()
  return getTsgToken(tsgId)
}
export const getCurrentAuthHeaders = (headers?: object) => getAuthHeaders(getCurrentTsgId(), headers)

const indexAndFilterTsgs = (items: Tsg[], jwt: string) => {
  if (getFrameworkVar('use_provided_tsg_token')) {
    const indexed = indexTsgTree(items)
    const decoded = jwtDecodeCached(jwt)
    const tsg = decoded?.tsg_id && decoded.tsg_id !== TSG_ID_NONE && indexed.get(decoded.tsg_id)
    if (tsg) {
      return indexTsgTree([{ ...tsg, children: undefined }])
    }
  }
  return indexTsgTree(items)
}
const loadTsgsCache = (email: string, url: string, jwt: string) => {
  if (sessionStorage.local_tsgs_cache) {
    const parsed = tryParseJson(sessionStorage.local_tsgs_cache)
    if (Array.isArray(parsed?.items) && parsed.items.length && parsed.email === email && parsed.url === url) {
      return indexAndFilterTsgs(parsed.items, jwt)
    }
  }
}
let tsgsPromise: Promise<ReadonlyTsgTree> | undefined
let initTsgLoading = true
export const fetchTsgs = async (jwt: string | undefined, email?: string): Promise<ReadonlyTsgTree> => {
  if (!jwt) {
    throw new Error('JWT token is required')
  }
  if (tsgsPromise) { // prevent multiple calls at the same time
    return tsgsPromise
  }
  const url = getUrlObject({
    baseUrl: getFrameworkServiceUrl(TENANCY_SERVICE_URL_KEY)!,
    path: '/tenant_service_groups',
    params: {
      hierarchy: 'true'
    },
  })?.toString()
  if (!url) {
    throw new Error('tenancy service url is not avaiable')
  }
  const cacheDuration = initTsgLoading ? FETCH_CACHING_DURATION : 1
  const promise = fetch(url, {
    method: 'GET',
    timeout: 10000, // 10s
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  }).then(async (res) => {
    if (res.ok) {
      initTsgLoading = false
      const data = await res.json()
      const items = data && (data.count >= 0 || !data.count) && Array.isArray(data.items) && data.items
      if (items) {
        setTimeout(() => {
          tsgsPromise = undefined
        }, cacheDuration)
        if (getFrameworkVar('local_tsgs_cache') && email) {
          data.email = email
          data.cached = Date.now()
          data.url = url
          sessionStorage.local_tsgs_cache = JSON.stringify(data)
        }
        return indexAndFilterTsgs(items, jwt)
      }
      else {
        tsgsPromise = undefined
        console.error('failed to fetch tenants tree', data, res)
        if (initTsgLoading && email && getFrameworkVar('local_tsgs_cache')) {
          const tsgs = loadTsgsCache(email, url, jwt)
          if (tsgs) {
            console.info('use cached tenants tree due to failed fetch', tsgs)
            return tsgs
          }
        }
        return indexTsgTree.fromError(data)
      }
    }
    const data = await res.text().then(tryParseErrorJson)
    tsgsPromise = undefined
    console.error('failed to fetch tenants tree', data, res)
    return indexTsgTree.fromError(data)
  }).catch((error: Error) => {
    tsgsPromise = undefined
    console.error('failed to fetch tenants tree', error)
    return indexTsgTree.fromError(error)
  })
  tsgsPromise = promise

  if (initTsgLoading && email && getFrameworkVar('local_tsgs_cache')) {
    const cached = loadTsgsCache(email, url, jwt)
    if (cached) {
      return new Promise((resolve) => {
        const ts = setTimeout(() => {
          resolve(cached)
        }, __isDebugOn() ? 1000 : FETCH_CACHING_DURATION)
        promise.then((res) => {
          clearTimeout(ts)
          resolve(res)
        })
      })
    }
  }

  return tsgsPromise
}

export const fetchTsgInstances = (tsg_id: string): Promise<ReadonlyTsgInstances> => {
  if (tsg_id === TSG_ID_NONE) {
    return Promise.resolve(indexTsgInstances([], TSG_ID_NONE))
  }

  const fetchPromiseFn = async (): Promise<ReadonlyTsgInstances> => {
    const baseUrl = getFrameworkServiceUrl(INSTANCE_STORE_URL_KEY)!
    const url = getUrlObject({
      baseUrl,
      path: '/instances',
      params: baseUrl?.includes('subscription') ? undefined : {
        tsg_id,
      },
    })
    if (!url) {
      const error = new Error('instance store url is not avaiable')
      Object.defineProperty(error, 'instances', { value: indexTsgInstances.fromError(error, tsg_id) })
      throw error
    }
    return fetch(url.toString(), {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      timeout: 10000, // 10s
      headers: await getAuthHeaders(tsg_id),
    }).then(async (res) => {
      if (res.ok) {
        const data = (await res.json()) as Instance[] | object
        if (Array.isArray(data)) {
          const insts = overrideTsgInstances(data as Instance[], tsg_id)
          return indexTsgInstances(insts, tsg_id)
        }
      }
      const data = await res.text().then(tryParseErrorJson)
      const msg = `failed to fetch instances for tsg: ${data?._error?.message || res.status}`
      const error = Object.assign(new Error(msg), data as object) as TsgInstanceError
      if (getFrameworkVar<boolean>('use_ip_restrictions') && data?._error?.code === '403' && data?._error?.message === 'API Access is IP restricted.') {
        error.ipRestricted = true
      }
      return indexTsgInstances.fromError(error, tsg_id)
    }, (error: Error) => {
      console.error('failed to fetch instances for tsg', error)
      return indexTsgInstances.fromError(error, tsg_id)
    })
  }

  return dedupPromise<ReadonlyTsgInstances>(`auth.instances:${tsg_id}`, fetchPromiseFn, { keepPromiseIn: FETCH_CACHING_DURATION })
}

// will be overridden when export and fill in definition automatically
export const getTsgAccess = async (tsg_id: string, rolePermissionDefinition?: IamRolePermissionDefinition): Promise<ReadonlyAccess | undefined> => {
  if (!tsg_id) {
    throw new Error('tsg_id is required to get IAM token access')
  }
  const jwt = await getTsgToken(tsg_id)
  const access = jwt && jwtDecodeCached(jwt, tsg_id)?.access
  const parsed = access ? parseAccess(access, rolePermissionDefinition) : undefined
  return parsed
}

export const fetchAppManifests = async (jwt: string | undefined): Promise<AppManifests> => {
  const baseUrl = getFrameworkServiceUrl(APP_REGISTRY_URL_KEY)
  if (!baseUrl) {
    throw new Error('app registry url is not avaiable')
  }
  const appsUrl = getUrlObject({
    baseUrl,
    path: '/apps',
  })!
  const fetchApps = async () => fetch(appsUrl.toString(), {
    method: 'GET',
    timeout: 10000, // 10s
    headers: {
      Authorization: `Bearer ${jwt || await getTsgToken(TSG_ID_NONE)}`,
    }
  }).then(async (res) => {
    if (res.ok) {
      const data = await res.json()
      if (Array.isArray(data)) {
        return indexAppManifests(data)
      }
    }
    const data = await res.text().then(tryParseErrorJson)
    console.error('failed to fetch app manifests', data)
    return indexAppManifests.fromError(new Error('failed to fetch app manifests'))
  }, (error: Error) => {
    console.error('failed to fetch app manifests', error)
    return indexAppManifests.fromError(error)
  })

  return dedupPromise<AppManifests>('apps_manifests', fetchApps, { keepPromiseIn: 10000 }) // 10s
}

export { parseAccess, fetchIamRolePermissionDefinition }
export const fetchIamCustomRoleDefinition = async (tsg_id: string | undefined): Promise<IamCustomRolesDefinition> => {
  if (!tsg_id) {
    throw new Error('tsg_id is required to get IAM custom roles')
  }
  if (tsg_id === TSG_ID_NONE) {
    return Object.assign(indexCustomRoles([]), { tsg_id })
  }
  const customRoles = await fetchIamCustomRoleDefinitionByJwt(await getTsgToken(tsg_id))
  return Object.assign(customRoles, { tsg_id })
}

export const setDatadogTsgIdContext = (tsg_id: string | undefined) => {
  if (!tsg_id) {
    return
  }
  const ddRum = getDatadogRum()
  if (!ddRum) {
    return
  }
  try {
    if (ddRum.setGlobalContextProperty) { // v5
      ddRum.setGlobalContextProperty('global_tsg_id', tsg_id)
    }
    else if ('addRumGlobalContext' in ddRum) { // v3
      (ddRum.addRumGlobalContext as RumGlobal['setGlobalContextProperty'])('global_tsg_id', tsg_id)
    }
  }
  catch (e) {
    __error('failed to set datadog context global_tsg_id', e)
  }
}
